// Reducer principal refactorisé avec délégation aux sous-reducers
import { MatchState, MatchAction, Player, PointEntry } from '../types';
import { handleBasicActions, handleServingActions } from './reducers';
import { isGameWon, isMatchWon } from '../utils';

// Ajouter l'action INIT_STATE aux types
type ExtendedMatchAction = MatchAction | { type: 'INIT_STATE'; state: MatchState };

export function matchReducer(state: MatchState, action: ExtendedMatchAction): MatchState {
  // Action spéciale pour réinitialiser complètement l'état
  if (action.type === 'INIT_STATE') {
    return action.state;
  }

  // Déléguer aux sous-reducers en premier
  let result = handleBasicActions(state, action as MatchAction);
  if (result) return result;
  
  result = handleServingActions(state, action as MatchAction);
  if (result) return result;
  
  // Actions complexes restent dans le reducer principal pour l'instant
  switch ((action as MatchAction).type) {
    case 'SCORE_POINT':
      const newState = { ...state };

      // Si aucun côté de service sélectionné, assigner "right" par défaut
      if (state.servingSide === null) {
        newState.servingSide = 'right';
      }

      // Start timers if not yet started (when side selection was skipped)
      if (state.matchStartTime === null) {
        newState.matchStartTime = Date.now();
      }
      if (state.gameStartTime === null) {
        newState.gameStartTime = Date.now();
      }

      // Save current state to history
      //newState.history = [...state.history, state];
      const stateForHistory = {
        ...state,
        history: [], // ← Éviter la récursion infinie
        activeTimer: null, // ← Timer is transient, don't save in history
        lastDecision: null
      };
      newState.history = [...state.history, stateForHistory];
      
      // Update scores
      newState.gameScores = {
        ...state.gameScores,
        [(action as any).player]: state.gameScores[(action as any).player] + 1
      };
      
      // Si on a une ligne de décision en cours, la compléter
      if (state.pendingDecisionLineIndex !== null) {
        const updatedPointsTable = [...state.pointsTable];
        const lineToUpdate = updatedPointsTable[state.pendingDecisionLineIndex];
        
        if ((action as any).player !== state.servingPlayer) {
          // Le receveur marque, il prend le service (Règle 4)
          // Entry et Ball au côté DROITE par défaut (modifiable)
          newState.servingPlayer = (action as any).player;
          newState.servingSide = 'right'; // Défaut: droite
          newState.canChangeServingSide = true; // Balle rouge (modifiable)

          // Mettre à jour la ligne avec le nouveau serveur
          updatedPointsTable[state.pendingDecisionLineIndex] = {
            ...lineToUpdate,
            playerA: newState.gameScores.A,
            playerB: newState.gameScores.B,
            servingPlayer: (action as any).player,
            servingSide: 'right' // Défaut: droite (entry = ball)
          };
          newState.pointsTable = updatedPointsTable;
          newState.pendingDecisionLineIndex = null; // Ligne complétée
        } else {
          // Le serveur marque, il garde le service et change de côté
          // Déterminer le côté effectif (utiliser le défaut 'right' si null)
          const effectiveSide = state.servingSide ?? 'right';
          // Le prochain service sera du côté OPPOSÉ
          const nextSide = effectiveSide === 'left' ? 'right' : 'left';

          newState.servingPlayer = (action as any).player;
          newState.servingSide = nextSide;
          newState.canChangeServingSide = false;

          // Compléter la ligne avec le côté où il VA servir (principe fondamental)
          updatedPointsTable[state.pendingDecisionLineIndex] = {
            ...lineToUpdate,
            playerA: newState.gameScores.A,
            playerB: newState.gameScores.B,
            servingPlayer: (action as any).player,
            servingSide: nextSide // Entry = Ball = côté où il VA servir
          };
          newState.pointsTable = updatedPointsTable;
          newState.pendingDecisionLineIndex = null; // Ligne complétée
        }
        
        newState.pendingDecision = null;
      } else {
        // Logique normale sans décision en cours
        if ((action as any).player !== state.servingPlayer) {
          // Le receveur marque - il prend le service (Règle 4)
          // Entry et Ball au côté DROITE par défaut (modifiable)
          newState.servingPlayer = (action as any).player;
          newState.servingSide = 'right'; // Défaut: droite
          newState.canChangeServingSide = true; // Balle rouge (modifiable)

          // Ajouter l'entrée IMMÉDIATEMENT au tableau
          // Elle sera mise à jour si le joueur choisit un autre côté
          const pointEntry: PointEntry = {
            playerA: newState.gameScores.A,
            playerB: newState.gameScores.B,
            servingPlayer: (action as any).player,
            servingSide: 'right', // Défaut: droite (entry = ball)
            decision: '',
            decisionFor: null
          };
          newState.pointsTable = [...state.pointsTable, pointEntry];
        } else {
          // Le serveur marque - il garde le service et change de côté
          // Déterminer le côté effectif (utiliser le défaut 'right' si null)
          const effectiveSide = state.servingSide ?? 'right';
          // Le prochain service sera du côté OPPOSÉ
          const nextSide = effectiveSide === 'left' ? 'right' : 'left';

          const pointEntry: PointEntry = {
            playerA: newState.gameScores.A,
            playerB: newState.gameScores.B,
            servingPlayer: state.servingPlayer,
            servingSide: nextSide,  // Entry au côté où il VA servir
            decision: '',
            decisionFor: null
          };
          newState.pointsTable = [...state.pointsTable, pointEntry];

          // Ball au MÊME côté que l'entry (principe fondamental)
          newState.servingPlayer = (action as any).player;
          newState.servingSide = nextSide;
          newState.canChangeServingSide = false;
        }
      }
      
      // Check if game is won using utility function
      const gameResult = isGameWon(newState.gameScores);

      if (gameResult.isWon && gameResult.winner) {
        // ✅ CRUCIAL : Sauvegarder l'état final AVANT toute modification
        // Cet état contient le score gagnant (ex: 11-0) qui doit être préservé
        const finalGameState = {
          ...state,
          history: newState.history // Éviter la récursion infinie dans l'historique
        };
        
        
        // Game won - mettre à jour les sets
        newState.setScores = {
          ...newState.setScores,
          [gameResult.winner]: newState.setScores[gameResult.winner] + 1
        };
        
        // Save game result
        const gameResultData = {
          winner: gameResult.winner,
          scoreA: newState.gameScores.A,
          scoreB: newState.gameScores.B,
          duration: Date.now() - (state.gameStartTime || Date.now()),
          setScoresAtEnd: {
            A: newState.setScores.A,
            B: newState.setScores.B
          }
        };
        newState.gameResults = [...state.gameResults, gameResultData];
        
        // Check if match is won using utility function
        const matchResult = isMatchWon(newState.setScores, state.matchInfo.scoringType);
        
        if (matchResult.isWon && matchResult.winner) {
          // Match terminé - attendre l'annonce avant de changer d'écran
          newState.matchEnded = true;
          newState.winner = matchResult.winner;
          newState.pendingMatchEnd = true; // Annonce d'abord, puis transition
          newState.totalMatchDuration = Date.now() - (state.matchStartTime || Date.now());
          
          // ✅ Ajouter l'état final à l'historique même en fin de match
          //newState.history = [...newState.history, finalGameState];
          const finalGameStateForHistory = {
            ...finalGameState,
            history: [],
            activeTimer: null,
            lastDecision: null
          };
          newState.history = [...newState.history, finalGameStateForHistory];
        } else {
          // Jeu suivant - NE PAS reset immédiatement, attendre l'annonce

          // ✅ AJOUTER l'état final avec le score gagnant à l'historique
          const finalGameStateForHistory = {
            ...finalGameState,
            history: [],
            activeTimer: null,
            lastDecision: null
          };
          newState.history = [...newState.history, finalGameStateForHistory];

          // Marquer le jeu comme en attente de fin (le reset sera fait par COMPLETE_GAME_END)
          newState.pendingGameEnd = true;
          newState.pendingGameEndInfo = {
            nextServingPlayer: gameResult.winner
          };
          // Les scores restent affichés jusqu'à ce que l'annonce soit terminée
          // Le reset sera effectué par l'action COMPLETE_GAME_END
        }
      }
      
      return newState;
    
    // Complète la fin de jeu (reset scores après l'annonce)
    case 'COMPLETE_GAME_END':
      return {
        ...state,
        gameScores: { A: 0, B: 0 },
        pointsTable: [],
        servingPlayer: state.pendingGameEndInfo?.nextServingPlayer || state.servingPlayer,
        servingSide: null,
        canChangeServingSide: true,
        pendingDecision: null,
        pendingDecisionLineIndex: null,
        pendingGameEnd: false,
        pendingGameEndInfo: undefined,
        gameStartTime: Date.now(),
      };

    // Les autres actions complexes restent ici pour l'instant...
    case 'STROKE_TO':
    case 'SET_DECISION':
    case 'WIN_GAME_INJURY':
    case 'CONDUCT_SANCTION':
    case 'COMPLETE_MATCH_END':
    case 'TIMER_FOR_PLAYER':
      // TODO: Ces actions seront extraites dans les prochaines étapes
      // Pour l'instant, on garde la logique originale complète

      // Fallback vers la logique originale complète pour ces actions
      return handleComplexActions(state, action as MatchAction);
    
    default:
      return state;
  }
}

// Fonction temporaire pour gérer les actions complexes non encore refactorisées
function handleComplexActions(state: MatchState, action: MatchAction): MatchState {
  // TODO: Cette fonction contient temporairement la logique originale
  // Elle sera supprimée quand toutes les actions seront refactorisées
  
  switch (action.type) {
    case 'STROKE_TO':
      // Pour STROKE TO, on doit d'abord s'assurer qu'une ligne de décision existe
      let strokeState = { ...state };

      // Créer une nouvelle ligne si :
      // - Pas de ligne en cours (pendingDecisionLineIndex === null), OU
      // - Décision précédente déjà traitée (pendingDecision === null, on attend juste le côté)
      if (state.pendingDecisionLineIndex === null || state.pendingDecision === null) {
        const incompleteEntry: PointEntry = {
          playerA: state.gameScores.A,
          playerB: state.gameScores.B,
          servingPlayer: null,
          servingSide: null,
          decision: 'STROKE',
          decisionFor: (action as any).player // Joueur qui reçoit le stroke
        };
        strokeState.pointsTable = [...state.pointsTable, incompleteEntry];
        strokeState.pendingDecisionLineIndex = strokeState.pointsTable.length - 1;
        strokeState.pendingDecision = 'STROKE';
      }
      strokeState.lastDecision = { type: 'STROKE', player: (action as any).player, timestamp: Date.now() };

      // Maintenant traiter le point
      return matchReducer(strokeState, { type: 'SCORE_POINT', player: (action as any).player });
    
    case 'SET_DECISION':
      const decisionState = { ...state };
      const decisionPlayer = (action as any).player; // Joueur qui a fait la demande

      if ((action as any).decision === 'LET') {
        // Pour LET, ajouter immédiatement une ligne complète au tableau avec le score actuel
        // Utiliser le côté effectif (défaut: right si null)
        const effectiveSide = state.servingSide ?? 'right';
        const letEntry: PointEntry = {
          playerA: state.gameScores.A,
          playerB: state.gameScores.B,
          servingPlayer: state.servingPlayer,
          servingSide: effectiveSide, // Côté actuel de la balle
          decision: 'LET',
          decisionFor: decisionPlayer // Joueur qui a demandé le LET
        };
        decisionState.pointsTable = [...state.pointsTable, letEntry];
        decisionState.servingSide = effectiveSide;
        decisionState.lastDecision = { type: 'LET', player: decisionPlayer, timestamp: Date.now() };
        // Le prochain point suivra l'alternance normale (entry et ball au côté OPPOSÉ)
      } else if ((action as any).decision === 'STROKE') {
        // Pour STROKE, ajouter une ligne incomplète qui sera complétée après sélection du joueur
        const incompleteEntry: PointEntry = {
          playerA: state.gameScores.A, // Score actuel avant le point
          playerB: state.gameScores.B, // Score actuel avant le point
          servingPlayer: null, // Sera complété après sélection
          servingSide: null,   // Sera complété après sélection
          decision: 'STROKE',
          decisionFor: decisionPlayer // Joueur qui a fait la demande
        };
        decisionState.pointsTable = [...state.pointsTable, incompleteEntry];
        decisionState.pendingDecisionLineIndex = state.pointsTable.length; // Index de la ligne à compléter
        decisionState.pendingDecision = 'STROKE';
        decisionState.lastDecision = { type: 'STROKE', player: decisionPlayer, timestamp: Date.now() };
      } else if ((action as any).decision === 'NOLET') {
        // Pour NOLET, le demandeur perd le point → attribuer à l'adversaire
        const opponent = decisionPlayer === 'A' ? 'B' : 'A';

        // Créer la ligne incomplète
        const incompleteEntry: PointEntry = {
          playerA: state.gameScores.A,
          playerB: state.gameScores.B,
          servingPlayer: null,
          servingSide: null,
          decision: 'NOLET',
          decisionFor: decisionPlayer // Joueur qui a fait la demande (et perdu)
        };
        decisionState.pointsTable = [...state.pointsTable, incompleteEntry];
        decisionState.pendingDecisionLineIndex = state.pointsTable.length; // Index de la nouvelle ligne
        decisionState.pendingDecision = 'NOLET';
        decisionState.lastDecision = { type: 'NOLET', player: decisionPlayer, timestamp: Date.now() };

        // Attribuer le point à l'adversaire
        return matchReducer(decisionState, { type: 'SCORE_POINT', player: opponent });
      }

      return decisionState;
    
    // WIN_GAME_INJURY et CONDUCT_SANCTION gardent leur logique originale pour l'instant
    case 'WIN_GAME_INJURY':
      const injuryState = { ...state };
      
      // Sauvegarder l'état avant l'action pour le rollback
      //injuryState.history = [...state.history, state];
      const injuryStateForHistory = {
        ...state,
        history: [],
        activeTimer: null,
        lastDecision: null
      };
      injuryState.history = [...state.history, injuryStateForHistory];

      // Attribuer le jeu au joueur (son score passe à 11)
      injuryState.gameScores = {
        ...state.gameScores,
        [(action as any).player]: 11
      };
      
      // Mettre à jour les scores de sets
      injuryState.setScores = {
        ...state.setScores,
        [(action as any).player]: state.setScores[(action as any).player] + 1
      };
      
      // Sauvegarder le résultat du jeu
      const injuryGameResult = {
        winner: (action as any).player,
        scoreA: (action as any).player === 'A' ? 11 : state.gameScores.A,
        scoreB: (action as any).player === 'B' ? 11 : state.gameScores.B,
        duration: Date.now() - (state.gameStartTime || Date.now()),
        wonByInjury: true,
        setScoresAtEnd: {
          A: injuryState.setScores.A,
          B: injuryState.setScores.B
        }
      };
      injuryState.gameResults = [...state.gameResults, injuryGameResult];
      
      // Vérifier si le match est terminé
      const setsToWin = state.matchInfo.scoringType === 'BestOf5' ? 3 : 2;
      if (injuryState.setScores[(action as any).player] >= setsToWin) {
        // Match gagné - attendre l'annonce avant de changer d'écran
        injuryState.matchEnded = true;
        injuryState.winner = (action as any).player;
        injuryState.pendingMatchEnd = true; // Annonce d'abord, puis transition
        injuryState.totalMatchDuration = Date.now() - (state.matchStartTime || Date.now());
      } else {
        // Préparer le jeu suivant - la popup de fin de jeu s'affichera automatiquement
        injuryState.gameScores = { A: 0, B: 0 };
        injuryState.pointsTable = [];
        injuryState.servingPlayer = (action as any).player; // Le gagnant sert en premier
        injuryState.servingSide = null;
        injuryState.canChangeServingSide = true;
        injuryState.pendingDecision = null;
        injuryState.pendingDecisionLineIndex = null;
        injuryState.gameStartTime = Date.now();
      }
      
      return injuryState;

    case 'CONDUCT_SANCTION':
      const conductState = { ...state };
      
      // Sauvegarder l'état avant la sanction pour le rollback
      //conductState.history = [...state.history, state];
      const conductStateForHistory = {
        ...state,
        history: [],
        activeTimer: null,
        lastDecision: null
      };
      conductState.history = [...state.history, conductStateForHistory];

      // Enregistrer la sanction
      const sanction = {
        type: (action as any).sanctionType as any,
        player: (action as any).player,
        justification: (action as any).justification,
        timestamp: Date.now(),
        gameNumber: state.gameResults.length + 1,
        gameScore: { A: state.gameScores.A, B: state.gameScores.B }
      };
      conductState.conductSanctions = [...state.conductSanctions, sanction];

      // Set lastDecision for live broadcast
      const conductDecisionMap: Record<string, string> = {
        conduct_warning: 'CW', conduct_point: 'CS', conduct_game: 'CG', conduct_match: 'CM'
      };
      const conductCode = conductDecisionMap[(action as any).sanctionType];
      if (conductCode) {
        conductState.lastDecision = { type: conductCode, player: (action as any).player, timestamp: Date.now() };
      }

      // Appliquer les conséquences selon le type de sanction
      switch ((action as any).sanctionType) {
        case 'conduct_warning':
          const warningEntry: PointEntry = {
            playerA: state.gameScores.A,
            playerB: state.gameScores.B,
            servingPlayer: state.servingPlayer,
            servingSide: state.servingSide,
            decision: 'CW',
            decisionFor: (action as any).player // Joueur sanctionné
          };
          conductState.pointsTable = [...state.pointsTable, warningEntry];
          break;
          
        case 'conduct_point':
          const opponent = (action as any).player === 'A' ? 'B' : 'A';
          conductState.gameScores = {
            ...state.gameScores,
            [opponent]: state.gameScores[opponent] + 1
          };

          const pointEntry: PointEntry = {
            playerA: conductState.gameScores.A,
            playerB: conductState.gameScores.B,
            servingPlayer: state.servingPlayer,
            servingSide: state.servingSide,
            decision: 'CS',
            decisionFor: (action as any).player // Joueur sanctionné
          };
          conductState.pointsTable = [...state.pointsTable, pointEntry];
          break;
          
        case 'conduct_game':
          const gameOpponent = (action as any).player === 'A' ? 'B' : 'A';
          const currentPlayerScore = state.gameScores[(action as any).player];

          let winningScore = Math.max(11, currentPlayerScore + 2);
          
          conductState.gameScores = {
            ...state.gameScores,
            [gameOpponent]: winningScore
          };
          
          const gameEntry: PointEntry = {
            playerA: conductState.gameScores.A,
            playerB: conductState.gameScores.B,
            servingPlayer: state.servingPlayer,
            servingSide: state.servingSide,
            decision: 'CG',
            decisionFor: (action as any).player // Joueur sanctionné
          };
          conductState.pointsTable = [...state.pointsTable, gameEntry];
          
          conductState.setScores = {
            ...state.setScores,
            [gameOpponent]: state.setScores[gameOpponent] + 1
          };
          
          const gameResult = {
            winner: gameOpponent as Player,
            scoreA: gameOpponent === 'A' ? winningScore : currentPlayerScore,
            scoreB: gameOpponent === 'B' ? winningScore : currentPlayerScore,
            duration: Date.now() - (state.gameStartTime || Date.now()),
            wonByConduct: true,
            setScoresAtEnd: {
              A: conductState.setScores.A,
              B: conductState.setScores.B
            }
          };
          conductState.gameResults = [...state.gameResults, gameResult];

          // ✅ CRUCIAL : Sauvegarder l'état avec le pointsTable contenant le CG
          // On utilise state.gameResults (AVANT ajout du résultat CG) pour que
          // getAllMatchPoints calcule le bon numéro de jeu (gameResults.length + 1)
          const stateWithCGForHistory = {
            ...conductState,
            gameResults: state.gameResults, // Ancien gameResults pour calcul correct du gameNumber
            history: [],
            activeTimer: null,
            lastDecision: null
          };
          conductState.history = [...conductState.history, stateWithCGForHistory];

          const setsToWin2 = state.matchInfo.scoringType === 'BestOf5' ? 3 : 2;
          if (conductState.setScores[gameOpponent] >= setsToWin2) {
            conductState.matchEnded = true;
            conductState.winner = gameOpponent;
            conductState.pendingMatchEnd = true; // Annonce d'abord, puis transition
            conductState.totalMatchDuration = Date.now() - (state.matchStartTime || Date.now());
          } else {
            conductState.gameScores = { A: 0, B: 0 };
            conductState.pointsTable = [];
            conductState.servingPlayer = gameOpponent;
            conductState.servingSide = null;
            conductState.canChangeServingSide = true;
            conductState.pendingDecision = null;
            conductState.pendingDecisionLineIndex = null;
            conductState.gameStartTime = Date.now();
          }
          break;
          
        case 'conduct_match':
          const matchOpponent = (action as any).player === 'A' ? 'B' : 'A';
          const requiredSets = state.matchInfo.scoringType === 'BestOf5' ? 3 : 2;
          
          conductState.setScores = {
            ...state.setScores,
            [matchOpponent]: requiredSets
          };
          
          const currentOpponentSets = state.setScores[matchOpponent];
          const hasCurrentGamePoints = state.gameScores.A > 0 || state.gameScores.B > 0;
          // If the current game has points, it counts as one of the sets won
          const setsFromAdditionalGames = requiredSets - currentOpponentSets - (hasCurrentGamePoints ? 1 : 0);

          const baseOpponentSets = currentOpponentSets + (hasCurrentGamePoints ? 1 : 0);
          const additionalGames: any[] = [];
          for (let i = 0; i < setsFromAdditionalGames; i++) {
            const currentSetsA = (matchOpponent === 'A' ? baseOpponentSets + i + 1 : state.setScores.A);
            const currentSetsB = (matchOpponent === 'B' ? baseOpponentSets + i + 1 : state.setScores.B);
            additionalGames.push({
              winner: matchOpponent as Player,
              scoreA: matchOpponent === 'A' ? 11 : 0,
              scoreB: matchOpponent === 'B' ? 11 : 0,
              duration: 0,
              wonByConduct: true,
              setScoresAtEnd: { A: currentSetsA, B: currentSetsB }
            });
          }

          if (hasCurrentGamePoints) {
            const currentGameResult = {
              winner: matchOpponent as Player,
              scoreA: matchOpponent === 'A' ? 11 : state.gameScores.A,
              scoreB: matchOpponent === 'B' ? 11 : state.gameScores.B,
              duration: Date.now() - (state.gameStartTime || Date.now()),
              wonByConduct: true,
              setScoresAtEnd: {
                A: state.setScores.A + (matchOpponent === 'A' ? 1 : 0),
                B: state.setScores.B + (matchOpponent === 'B' ? 1 : 0)
              }
            };
            conductState.gameResults = [...state.gameResults, currentGameResult, ...additionalGames];
          } else {
            conductState.gameResults = [...state.gameResults, ...additionalGames];
          }
          
          conductState.matchEnded = true;
          conductState.winner = matchOpponent;
          conductState.pendingMatchEnd = true; // Annonce d'abord, puis transition
          conductState.totalMatchDuration = Date.now() - (state.matchStartTime || Date.now());
          break;
      }
      
      return conductState;

    case 'COMPLETE_MATCH_END':
      // Transition vers l'écran After Match après l'annonce
      return {
        ...state,
        currentScreen: 'after',
        pendingMatchEnd: false,
      };

    case 'TIMER_FOR_PLAYER':
      const timerState = { ...state };

      // Sauvegarder l'état avant l'action pour le rollback
      const timerStateForHistory = {
        ...state,
        history: [],
        activeTimer: null,
        lastDecision: null
      };
      timerState.history = [...state.history, timerStateForHistory];

      // Ajouter une entrée dans le pointsTable pour tracer le timer
      const timerEntry: PointEntry = {
        playerA: state.gameScores.A,
        playerB: state.gameScores.B,
        servingPlayer: state.servingPlayer,
        servingSide: state.servingSide,
        decision: (action as any).timerType, // EQP, INJ, BLD, SHR
        decisionFor: (action as any).player, // Joueur concerné par le timer
        timerDuration: (action as any).duration // Durée en secondes
      };
      timerState.pointsTable = [...state.pointsTable, timerEntry];

      return timerState;

    default:
      return state;
  }
}